import {
  ApolloQueryResult,
  DocumentNode,
  FetchMoreQueryOptions,
  NetworkStatus,
  OperationVariables,
  QueryHookOptions,
  QueryOptions,
  TypedDocumentNode,
  useApolloClient,
} from '@apollo/client';
import { useEffect, useMemo, useRef } from 'react';
import { SuspenseQueryManager } from './suspense_query_manager';
import { createHash, useReloadKey } from './util';

const suspenseQueryManager = new SuspenseQueryManager();

export function useSuspenseQuery<TData, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: Omit<QueryOptions<TVariables, TData>, 'query'> & QueryHookOptions<TData, TVariables>
): {
  data: TData;
  fetchMore: (fetchMoreOptions: FetchMoreQueryOptions<TVariables, TData>) => Promise<ApolloQueryResult<TData>>;
  loadingMore: boolean;
} {
  const apolloClient = useApolloClient();
  const [_, reload] = useReloadKey();

  const currentQueryId = useRef<string | null>(null);

  const uniqueQueryId = useMemo(() => {
    return createHash(JSON.stringify([query, options?.variables]));
  }, [query, options?.variables]);

  // todo: this might not always execute as expected. when changing variables,
  // the uniqueQueryId may not change so this does not delete the previous query.
  if (currentQueryId.current !== null && currentQueryId.current !== uniqueQueryId) {
    suspenseQueryManager.deleteById(currentQueryId.current);
    currentQueryId.current = uniqueQueryId;
  }

  const mergedOptions: QueryOptions<TVariables, TData> = { ...options, query };
  const { query: apolloQuery, observable } = suspenseQueryManager.getOrCreateRecord(
    uniqueQueryId,
    apolloClient,
    mergedOptions
  );

  useEffect(() => {
    observable.subscribe(reload);
    return () => {
      observable.unsubscribe(reload);
    };
  }, [reload, observable]);

  const queryResult = observable.readSuspended();
  useEffect(() => {
    return () => {
      suspenseQueryManager.deleteById(uniqueQueryId);
    };
  }, []);

  return {
    data: queryResult.data,
    fetchMore: apolloQuery.fetchMore.bind(apolloQuery),
    loadingMore: queryResult.networkStatus === NetworkStatus.fetchMore,
  };
}
